import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Store, Upload, Camera, CheckCircle, Info, Save, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TPVConfigSection = ({ businessId }) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState({
    tpv_brand: '',
    business_cif: '',
    ticket_format: 'standard',
    sample_ticket_image: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, [businessId]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/business/tpv-config`, {
        headers: { 'X-Business-Id': businessId }
      });
      setConfig(prev => ({ ...prev, ...response.data }));
      if (response.data.sample_ticket_image) {
        setImagePreview(response.data.sample_ticket_image);
      }
    } catch (error) {
      console.log('No TPV config yet');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen es demasiado grande (máx 5MB)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setConfig(prev => ({ ...prev, sample_ticket_image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/business/tpv-config`, config, {
        headers: { 
          'X-Business-Id': businessId,
          'Content-Type': 'application/json'
        }
      });
      toast.success(t('tpv_config.save_success') || 'Configuración TPV guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border/50">
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
      data-testid="tpv-config-section"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center text-white">
          <Store className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">{t('tpv_config.title') || 'Configuración del TPV'}</h3>
          <p className="text-sm text-muted-foreground">
            {t('tpv_config.description') || 'Configura tu Terminal de Punto de Venta para verificación de tickets'}
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <p className="font-medium">{t('tpv_config.instructions_title') || '¿Por qué configurar el TPV?'}</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
              <li>Verificamos automáticamente los tickets de tus clientes</li>
              <li>Asegura que el ticket coincide con tu negocio</li>
              <li>Los clientes ganan puntos más rápido</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* TPV Brand */}
        <div>
          <Label htmlFor="tpv_brand">{t('tpv_config.brand_label') || 'Marca del TPV'}</Label>
          <Input
            id="tpv_brand"
            value={config.tpv_brand}
            onChange={(e) => setConfig(prev => ({ ...prev, tpv_brand: e.target.value }))}
            placeholder={t('tpv_config.brand_placeholder') || 'Ej: Sumup, Zettle, Ingenico...'}
            className="mt-2"
            data-testid="tpv-brand-input"
          />
        </div>

        {/* Business CIF */}
        <div>
          <Label htmlFor="business_cif">{t('tpv_config.cif_label') || 'CIF/NIF del negocio'}</Label>
          <Input
            id="business_cif"
            value={config.business_cif}
            onChange={(e) => setConfig(prev => ({ ...prev, business_cif: e.target.value }))}
            placeholder="B12345678"
            className="mt-2"
            data-testid="business-cif-input"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Se usa para validar que el ticket pertenece a tu negocio
          </p>
        </div>

        {/* Ticket Format */}
        <div>
          <Label>{t('tpv_config.format_label') || 'Formato del ticket'}</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {[
              { value: 'standard', label: 'Estándar', desc: 'Ticket térmico normal' },
              { value: 'receipt_paper', label: 'Papel continuo', desc: 'Impresora de recibos' },
              { value: 'digital', label: 'Digital', desc: 'Email o PDF' },
              { value: 'qr_code', label: 'Con QR', desc: 'Incluye código QR' }
            ].map(format => (
              <button
                key={format.value}
                onClick={() => setConfig(prev => ({ ...prev, ticket_format: format.value }))}
                className={`p-3 rounded-xl border text-left transition-all ${
                  config.ticket_format === format.value
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                    : 'border-border hover:border-emerald-300'
                }`}
                data-testid={`ticket-format-${format.value}`}
              >
                <p className="font-medium text-sm">{format.label}</p>
                <p className="text-xs text-muted-foreground">{format.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Sample Ticket Upload */}
        <div>
          <Label>{t('tpv_config.sample_ticket_label') || 'Ticket de ejemplo (opcional)'}</Label>
          <p className="text-xs text-muted-foreground mb-3">
            {t('tpv_config.sample_ticket_desc') || 'Sube un ticket de ejemplo para mejorar la verificación automática'}
          </p>
          
          <div className="flex gap-4 items-start">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
                data-testid="ticket-image-input"
              />
              <div className="w-32 h-40 border-2 border-dashed border-muted-foreground/30 rounded-xl flex flex-col items-center justify-center hover:border-emerald-500 transition-colors">
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt="Ticket preview" 
                    className="w-full h-full object-cover rounded-xl"
                  />
                ) : (
                  <>
                    <Camera className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-xs text-muted-foreground">Subir foto</span>
                  </>
                )}
              </div>
            </label>
            
            {imagePreview && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Imagen subida</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setImagePreview(null);
                    setConfig(prev => ({ ...prev, sample_ticket_image: null }));
                  }}
                >
                  Eliminar
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          data-testid="save-tpv-config-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t('tpv_config.save_btn') || 'Guardar configuración'}
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default TPVConfigSection;
